import { Box, Typography } from '@mui/material';
import { ReactNode } from 'react';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { cardStyle } from 'src/modules/sending/components/sendingWizardCardStyles';

// Höger kolumn: förhandsvisning i ljusgrå ruta. basis styr bredden.
export const PreviewPane = ({ children, basis = '40%' }: { children: ReactNode; basis?: string }) => (
  <Box sx={{ flex: { md: `0 0 ${basis}` }, minWidth: 0, position: { md: 'relative' } }}>
    <Box
      sx={{
        borderLeft: '1px solid',
        borderColor: 'divider',
        pt: 0,
        px: 4,
        pb: 4,
        position: { md: 'absolute' },
        inset: { md: 0 },
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 1 }}>
        Förhandsvisning
      </Typography>
      {/* Grå "iframe"-yta så den vita previewen framhävs. */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', bgcolor: 'grey.100', borderRadius: 1, p: 2 }}>{children}</Box>
    </Box>
  </Box>
);

interface TextEditorWithPreviewProps {
  label: string;
  helper?: string;
  // Formulärfältet för rubriken.
  headingName: string;
  headingLabel?: string;
  headingDisabled?: boolean;
  // Grönt kort runt rubrik och editor, används när texten är klarmarkerad.
  highlighted?: boolean;
  // Knapp som visas både överst och under editorn, till exempel Klar eller Ändra.
  action?: ReactNode;
  editor: ReactNode;
  preview: ReactNode;
  previewBasis?: string;
}

// En textrad: rubrik och hjälptext med knapp högst upp, sedan rubrikfält och editor, och
// förhandsvisningen till höger. Knappen upprepas under editorn så den syns utan att scrolla upp.
//
// Delas av utskicksbeställningen och fakturamallens redigering, så att samma arbete ser likadant
// ut på båda ställena.
export const TextEditorWithPreview = ({
  label,
  helper,
  headingName,
  headingLabel = 'Rubrik',
  headingDisabled = false,
  highlighted = false,
  action,
  editor,
  preview,
  previewBasis,
}: TextEditorWithPreviewProps) => (
  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'stretch', mb: 3, minHeight: { md: 720 } }}>
    <Box sx={{ flex: { md: '0 0 58%' }, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant='h6' sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {label}
          </Typography>
          {helper && (
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.25 }}>
              {helper}
            </Typography>
          )}
        </Box>
        {action}
      </Box>
      <Box sx={highlighted ? { ...cardStyle(true), mt: 2 } : { mt: 2 }}>
        <Box sx={{ mb: 2 }}>
          <ControlledTextField name={headingName} label={headingLabel} fullWidth disabled={headingDisabled} />
        </Box>
        {editor}
      </Box>
      {action && <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>{action}</Box>}
    </Box>

    <PreviewPane basis={previewBasis}>{preview}</PreviewPane>
  </Box>
);
